'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useAnalytics } from '@/hooks/useAnalytics';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Communication features data
const communicationFeatures = [
  {
    title: 'Community Forums',
    description: 'Create dedicated spaces for team discussions, announcements, and community building.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
    color: 'blue',
  },
  {
    title: 'Direct Messaging',
    description: 'Enable private conversations between coaches, players, and parents with secure, instant messaging.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
      </svg>
    ),
    color: 'indigo',
  },
  {
    title: 'Group Messaging',
    description: 'Create team, position, or event-specific groups to streamline communication and keep everyone informed.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
      </svg>
    ),
    color: 'purple',
  },
  {
    title: 'SMS Notifications',
    description: 'Send important updates via text message to ensure critical information reaches everyone immediately.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
      </svg>
    ),
    color: 'green',
  },
  {
    title: 'Email Communications',
    description: 'Send formatted emails with attachments for more formal communications and newsletters.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
      </svg>
    ),
    color: 'red',
  },
  {
    title: 'Push Notifications',
    description: 'Keep users engaged with timely mobile app notifications for important updates and events.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
      </svg>
    ),
    color: 'amber',
  },
];

// Benefits data
const benefits = [
  {
    title: 'Centralized Communication',
    description: 'No more juggling between email, text, and messaging apps. Keep all communications in one place.',
  },
  {
    title: 'Improved Coordination',
    description: 'Ensure everyone is on the same page with clear, timely updates about practices, games, and events.',
  },
  {
    title: 'Enhanced Engagement',
    description: 'Foster a stronger sense of community and increase participation with interactive communication tools.',
  },
  {
    title: 'Time Savings',
    description: 'Reduce administrative overhead with templates, scheduled messages, and automated notifications.',
  },
];

// Use cases
const useCases = [
  {
    title: 'Youth Sports Clubs',
    description: 'Keep parents informed about schedule changes, upcoming events, and important announcements.',
    icon: '🏆',
  },
  {
    title: 'High School Athletics',
    description: 'Coordinate with coaches, athletes, and parents across multiple sports programs efficiently.',
    icon: '🏫',
  },
  {
    title: 'College Sports Programs',
    description: 'Maintain clear communication channels between coaching staff, athletes, and administration.',
    icon: '🎓',
  },
  {
    title: 'Professional Teams',
    description: 'Streamline internal communications and manage media relations from a single platform.',
    icon: '🏅',
  },
];

export default function CommunicationFeaturePage() {
  const { trackFeature } = useAnalytics();
  
  React.useEffect(() => {
    trackFeature('communication', 'page_view');
  }, [trackFeature]);

  return (
    <div className="bg-futuristic-black min-h-screen text-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-futuristic-black"></div>
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
        </div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-blue-500/10 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-indigo-500/10 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="text-center lg:text-left"
            >
              <h1 className="font-kanit italic font-bold text-4xl md:text-6xl tracking-tight mb-6">
                <span className="block text-white">SEAMLESS</span>
                <span className="block bg-gradient-to-r from-blue-400 via-athletic-highlight to-athletic-accent bg-clip-text text-transparent">
                  COMMUNICATION
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl">
                Keep everyone connected with direct messaging, group chats, SMS, email, and push notifications. Simplify your sports communication and ensure no one misses important updates.
              </p>
              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                <Button
                  variant="athletic"
                  size="xl"
                  href="/demo"
                  trackingName="communication_demo_button"
                  trackingLocation="communication_feature_page"
                  className="font-kanit"
                >
                  GET A DEMO
                </Button>
                <Button
                  variant="minimal"
                  size="xl"
                  href="/features"
                  trackingName="communication_features_button"
                  trackingLocation="communication_feature_page"
                  className="font-kanit"
                >
                  EXPLORE ALL FEATURES
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="relative"
            >
              <div className="relative rounded-xl overflow-hidden shadow-athletic-blue">
                {/* Feature image with glassmorphism effect */}
                <div className="aspect-[4/3] bg-gradient-to-br from-blue-500/10 to-indigo-600/10 flex items-center justify-center text-white relative">
                  <div className="absolute inset-0 bg-futuristic-black/80 backdrop-blur-md"></div>
                  <div className="relative z-10 p-6 w-full max-w-md mx-auto">
                    <div className="border border-white/10 rounded-2xl p-4 backdrop-blur-md bg-black/40 shadow-xl">
                      {/* Window Controls */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="flex space-x-2">
                            <div className="w-3 h-3 rounded-full bg-red-500/90"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-500/90"></div>
                            <div className="w-3 h-3 rounded-full bg-green-500/90"></div>
                          </div>
                        </div>
                        <h3 className="font-kanit text-lg font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                          TEAM MESSAGING
                        </h3>
                        <div className="w-16"></div> {/* Spacer for alignment */}
                      </div>

                      {/* Chat Messages */}
                      <div className="space-y-4 mb-6">
                        <div className="flex items-start space-x-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm shadow-lg ring-2 ring-blue-500/20">
                            C
                          </div>
                          <div className="flex-1">
                            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl rounded-tl-none p-3 shadow-lg">
                              <p className="text-sm text-white/90">Practice moved to Field 3 today due to maintenance</p>
                              <p className="text-[10px] text-blue-300 text-right mt-1">10:15 AM</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start justify-end space-x-3">
                          <div className="flex-1">
                            <div className="bg-blue-600/20 backdrop-blur-sm border border-blue-500/20 rounded-2xl rounded-tr-none p-3 shadow-lg ml-auto max-w-[90%]">
                              <p className="text-sm text-white/90">Got it, thanks for the update!</p>
                              <p className="text-[10px] text-blue-300 text-right mt-1">10:17 AM</p>
                            </div>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-lg ring-2 ring-indigo-500/20">
                            P
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-lg ring-2 ring-purple-500/20">
                            M
                          </div>
                          <div className="flex-1">
                            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl rounded-tl-none p-3 shadow-lg">
                              <p className="text-sm text-white/90">Should we bring extra equipment?</p>
                              <p className="text-[10px] text-blue-300 text-right mt-1">10:18 AM</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Team Members */}
                      <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3 shadow-lg">
                        <div className="flex justify-between items-center mb-3">
                          <p className="text-sm font-medium text-white/80">TEAM MEMBERS</p>
                          <p className="text-xs bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent font-medium cursor-pointer hover:opacity-80 transition-opacity">
                            VIEW ALL
                          </p>
                        </div>
                        <div className="flex -space-x-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 border-2 border-black flex items-center justify-center text-white text-xs font-medium shadow-lg">JD</div>
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 border-2 border-black flex items-center justify-center text-white text-xs font-medium shadow-lg">KL</div>
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 border-2 border-black flex items-center justify-center text-white text-xs font-medium shadow-lg">MR</div>
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-green-600 border-2 border-black flex items-center justify-center text-white text-xs font-medium shadow-lg">TS</div>
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 border-2 border-black flex items-center justify-center text-white text-xs font-medium shadow-lg">+8</div>
                        </div>
                      </div>

                      {/* New Message Input */}
                      <div className="mt-4 flex items-center space-x-2">
                        <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-2">
                          <p className="text-white/50 text-sm">Type a message...</p>
                        </div>
                        <button className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg hover:opacity-90 transition-opacity">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-futuristic-black via-blue-900/10 to-futuristic-black"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="font-kanit italic font-bold text-3xl md:text-5xl text-white mb-4">
              COMMUNICATION <span className="text-athletic-highlight">FEATURES</span>
            </h2>
            <p className="text-xl text-gray-300">
              Everything you need to keep your sports organization connected and informed
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {communicationFeatures.map((feature) => (
              <motion.div
                key={feature.title}
                variants={fadeIn}
                className="bg-futuristic-black border border-white/10 rounded-xl shadow-lg hover:shadow-athletic-blue transition-all duration-300 overflow-hidden group hover:-translate-y-1"
              >
                <div className="p-8">
                  <div className={`w-12 h-12 rounded-lg bg-${feature.color}-500/20 text-${feature.color}-400 flex items-center justify-center mb-6 group-hover:shadow-${feature.color}-glow transition-all duration-300`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-kanit font-bold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Streamlined communication that keeps everyone in the loop
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeIn}
              className="order-2 lg:order-1"
            >
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-lg mr-4">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Create Communication Channels</h3>
                    <p className="text-gray-600">
                      Set up team-wide, position-specific, or event-based communication channels to organize conversations.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-lg mr-4">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Send Multi-Channel Messages</h3>
                    <p className="text-gray-600">
                      Compose messages once and deliver them across multiple channels (app, email, SMS) based on urgency and preference.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-lg mr-4">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Track Engagement</h3>
                    <p className="text-gray-600">
                      See who has viewed important messages and send follow-ups to those who haven't engaged yet.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-lg mr-4">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Automate Routine Communications</h3>
                    <p className="text-gray-600">
                      Set up automated reminders for practices, games, and events to reduce administrative workload.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeIn}
              className="order-1 lg:order-2"
            >
              <div className="relative rounded-xl overflow-hidden shadow-xl">
                {/* Placeholder for how it works image - replace with actual image */}
                <div className="aspect-[4/3] bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white">
                  <p className="text-xl font-semibold">Communication Flow Diagram</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Benefits of Unified Communication
            </h2>
            <p className="text-xl text-gray-600">
              Why sports organizations choose Sketchplay for their communication needs
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className="bg-blue-50 rounded-xl p-8"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Who Uses Our Communication Tools
            </h2>
            <p className="text-xl text-gray-600">
              Solutions for sports organizations of all sizes
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className="bg-white rounded-xl shadow-lg p-8 text-center"
              >
                <div className="text-4xl mb-4">
                  {useCase.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {useCase.title}
                </h3>
                <p className="text-gray-600">
                  {useCase.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-blue-600 rounded-2xl shadow-xl p-8 md:p-12 text-white">
              <svg
                className="h-12 w-12 opacity-25 text-white mb-6"
                fill="currentColor"
                viewBox="0 0 32 32"
                aria-hidden="true"
              >
                <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
              </svg>
              
              <p className="text-xl md:text-2xl font-medium mb-8">
                "Sketchplay's communication tools have transformed how we interact with our 500+ youth soccer families. We've reduced missed practices by 35% and parent satisfaction scores have increased dramatically. The ability to send targeted messages to specific teams or age groups has been a game-changer."
              </p>
              
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-14 w-14 rounded-full bg-white flex items-center justify-center text-blue-600 text-xl font-bold">
                    MJ
                  </div>
                </div>
                <div className="ml-4">
                  <div className="text-lg font-medium">Michael Johnson</div>
                  <div className="text-blue-200">
                    Director, Elite Youth Soccer Club
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to streamline your sports communication?
            </h2>
            <p className="text-xl text-blue-100 mb-10">
              Join thousands of sports organizations that trust Sketchplay to keep everyone connected and informed.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                variant="default"
                size="xl"
                href="/demo"
                className="bg-white text-blue-700 hover:bg-blue-50"
                trackingName="communication_cta_demo_button"
                trackingLocation="communication_feature_page"
              >
                Get a Demo
              </Button>
              <Button
                variant="outline"
                size="xl"
                href="/features"
                className="border-white text-white hover:bg-white/10"
                trackingName="communication_cta_features_button"
                trackingLocation="communication_feature_page"
              >
                Explore All Features
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
} 